import { useTranslation } from 'react-i18next';
import { RecurrenceConfig, getRecurrenceLabelFromConfig } from '../../types';

interface RecurrenceLegendProps {
  config: RecurrenceConfig | null | undefined;
  operationDate?: string;
  className?: string;
}

/** Construit une date à midi UTC depuis année/mois(0-11)/jour, en bornant au dernier jour du mois. */
function makeUTCDate(year: number, monthIndex: number, day: number): Date {
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0, 12, 0, 0, 0)).getUTCDate();
  return new Date(Date.UTC(year, monthIndex, Math.min(day, lastDay), 12, 0, 0, 0));
}

/** Calcule l'occurrence adjacente (précédente ou suivante) selon la fréquence. */
function getAdjacentOccurrence(
  config: RecurrenceConfig,
  date: Date,
  direction: 1 | -1
): Date {
  const day = date.getUTCDate();
  switch (config.frequency) {
    case 'WEEKLY':
    case 'EVERY_N_WEEKS': {
      const interval = config.interval || 1;
      const d = new Date(date.getTime());
      d.setUTCDate(d.getUTCDate() + direction * interval * 7);
      return d;
    }
    case 'EVERY_N_MONTHS': {
      const interval = config.interval || 2;
      return makeUTCDate(date.getUTCFullYear(), date.getUTCMonth() + direction * interval, day);
    }
    case 'YEARLY':
      return makeUTCDate(date.getUTCFullYear() + direction, date.getUTCMonth(), day);
    default:
      // MONTHLY_DAY, MONTHLY_WEEKDAY, INSTALLMENTS : pas mensuel approximé au mois adjacent
      return makeUTCDate(date.getUTCFullYear(), date.getUTCMonth() + direction, day);
  }
}

function isWithin(date: Date, start: Date, end: Date): boolean {
  return date.getTime() >= start.getTime() && date.getTime() <= end.getTime();
}

/**
 * Composant qui affiche une légende lisible pour la configuration de récurrence
 * Exemples:
 * - "Mensuel le 15"
 * - "2ème samedi du mois"
 * - "Chaque samedi"
 * - "Toutes les 2 semaines"
 * - "Paiement 2/4"
 *
 * Affiche en plus une ligne de pause de prélèvement :
 * - "Pause du JJ/MM au JJ/MM" sur la dernière occurrence avant la fenêtre
 * - "Reprise après pause" sur la première occurrence après la fenêtre
 */
export default function RecurrenceLegend({
  config,
  operationDate,
  className = '',
}: RecurrenceLegendProps) {
  const { t, i18n } = useTranslation('common');
  if (!config) return null;

  const label = getRecurrenceLabelFromConfig(config, operationDate);

  if (!label) return null;

  // Ajouter l'info de date de fin si présente
  let endDateLabel = '';
  if (config.endDate && config.frequency !== 'INSTALLMENTS') {
    const endDate = new Date(config.endDate);
    endDateLabel = ` ${t('recurrence.untilDate', { date: endDate.toLocaleDateString(i18n.language) })}`;
  }

  // Déterminer si l'opération courante est une frontière de l'une des pauses
  let pauseLabel = '';
  if (config.pauses && config.pauses.length > 0 && operationDate) {
    const opDate = new Date(operationDate);
    for (const p of config.pauses) {
      if (!p.start || !p.end) continue;
      const pauseStart = new Date(p.start);
      const pauseEnd = new Date(p.end);

      if (opDate.getTime() < pauseStart.getTime()) {
        // Dernière occurrence avant la pause : l'occurrence suivante tombe-t-elle dans la fenêtre ?
        const next = getAdjacentOccurrence(config, opDate, 1);
        if (isWithin(next, pauseStart, pauseEnd)) {
          pauseLabel = t('recurrence.pausedFrom', {
            start: pauseStart.toLocaleDateString(i18n.language),
            end: pauseEnd.toLocaleDateString(i18n.language),
          });
          break;
        }
      } else if (opDate.getTime() > pauseEnd.getTime()) {
        // Première occurrence après la pause : l'occurrence précédente tombait-elle dans la fenêtre ?
        const prev = getAdjacentOccurrence(config, opDate, -1);
        if (isWithin(prev, pauseStart, pauseEnd)) {
          pauseLabel = t('recurrence.resumesAfterPause');
          break;
        }
      }
    }
  }

  return (
    <span className={`flex flex-col ${className}`}>
      <span
        className="text-xs text-gray-500 truncate"
        title={label + endDateLabel}
      >
        {label}{endDateLabel}
      </span>
      {pauseLabel && (
        <span className="text-xs text-amber-600 truncate" title={pauseLabel}>
          {pauseLabel}
        </span>
      )}
    </span>
  );
}
